import React, { useState } from 'react';
import clsx from 'clsx';

import { AppContext } from './contexts';
import { useInterval } from '../hooks';
import { WelcomeScreen } from './components/WelcomeScreen';
import {
  stepComponentMapping,
  tabs,
  getActiveTab,
  getEnabledTabs,
  getLastCompletedStep,
  getNextStep,
  getDataForStep,
  wasStepAlreadyCompleted,
} from './utils';

export default function App({
  APIs,
  estimatedTimes,
  taskPollInterval,
  downloadFiles,
}) {
  const [showingWelcomeScreen, setShowingWelcomeScreen] = useState(true);
  const [activeProject, setActiveProject] = useState();
  const [visibleStep, setVisibleStep] = useState('projects');
  const lastCompletedStep = getLastCompletedStep(activeProject?.steps);
  const enabledTabs = getEnabledTabs(tabs, lastCompletedStep);
  const activeTab = getActiveTab(visibleStep);

  const handleTabChange = (tabId) => {
    const stepAfterLastCompleted = getNextStep(lastCompletedStep);
    const tabToChangeIndex = tabs.findIndex(({ id }) => id == tabId);
    const tabWithStepAfterLastCompleted = tabs.findIndex(({ steps }) =>
      steps.includes(stepAfterLastCompleted),
    );
    const tabToChangeSteps = tabs[tabToChangeIndex].steps;

    if (tabToChangeIndex == tabWithStepAfterLastCompleted) {
      setVisibleStep(stepAfterLastCompleted);
    } else if (tabToChangeIndex < tabWithStepAfterLastCompleted) {
      setVisibleStep(tabToChangeSteps[tabToChangeSteps.length - 1]);
    } else {
      setVisibleStep(tabToChangeSteps[0]);
    }
  };
  const StepComponent = stepComponentMapping[visibleStep];
  const stepComponentData = activeProject?.steps
    ? getDataForStep(visibleStep, activeProject.steps)
    : null;
  const isStepCompleted = wasStepAlreadyCompleted(
    visibleStep,
    lastCompletedStep,
  );

  const value = {
    APIs,
    activeProject,
    setActiveProject,
    setVisibleStep,
    lastCompletedStep,
    estimatedTimes,
    downloadFiles,
  };

  const areTasksInQueue = activeProject
    ? activeProject.steps.some(({ status }) => status == 'running')
    : false;
  const shouldPollTasks = areTasksInQueue;

  useInterval(
    () => {
      // update the active project data which will update
      // state of task in the queue if it finished
      // this could be done better probably, by utilizing the
      // the already made polling by the website tasks modal.
      APIs.projects.getProject(activeProject.id).then((data) => {
        setActiveProject(data);
      });
    },
    !shouldPollTasks
      ? null
      : taskPollInterval
        ? taskPollInterval * 1000
        : 30 * 1000,
  );

  return (
    <AppContext.Provider value={value}>
      <div className="hero min-h-[60vh] bg-base-200/60 xl:hidden">
        <div className="hero-content text-center max-w-xl">
          <div className="card card-bordered bg-base-100 border-success">
            <div className="card-body items-center gap-4">
              <div className="badge badge-success badge-outline px-4 py-3 gap-2">
                <span className="text-sm font-semibold tracking-wide uppercase">
                  Norm AIR
                </span>
                <span className="text-xs font-medium normal-case opacity-80">
                  1280&nbsp;px+
                </span>
              </div>
              <h2 className="card-title text-center">
                Pełny panel dostępny na większym ekranie
              </h2>
              <p className="text-base-content/70">
                Panel Norm AIR korzysta z rozbudowanych wykresów i tabel, które
                do komfortowej pracy wymagają przestrzeni roboczej dostępnej na
                urządzeniach desktopowych.
              </p>
              <p className="text-sm text-base-content/60">
                Powiększ okno przeglądarki lub przełącz się na monitor o
                szerokości co najmniej 1280&nbsp;px, aby kontynuować pracę w
                aplikacji Norm AIR.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="hidden xl:block">
        {showingWelcomeScreen ? (
          <WelcomeScreen setShowingWelcomeScreen={setShowingWelcomeScreen} />
        ) : (
          <div className="w-full mx-auto rounded-lg mb-10 [--app-radius:0.5rem] [--border-color:--color-slate-600]">
            <div
              role="tablist"
              className="tabs tabs-boxed rounded-b-none bg-base-300"
            >
              {tabs.map((tab, i) => (
                <a
                  role="tab"
                  className={clsx(
                    'tab',
                    activeTab.name == tab.name && 'tab-active',
                    !enabledTabs.includes(tab.id) && 'tab-disabled',
                  )}
                  onClick={() =>
                    enabledTabs.includes(tab.id) &&
                    activeTab.name !== tab.name &&
                    handleTabChange(tab.id)
                  }
                  key={tab + i}
                  aria-disabled={!enabledTabs.includes(tab.id)}
                >
                  {tab.name}
                </a>
              ))}
            </div>
            <StepComponent
              data={stepComponentData}
              name={visibleStep}
              isCompleted={isStepCompleted}
            />
          </div>
        )}
      </div>
    </AppContext.Provider>
  );
}
